import React from 'react';
import { useSite } from '../context/SiteContext';
import { useLang } from '../context/LangContext';
import { pickI18n } from '../i18n/helpers';
import { ArrowUpRight, Sparkles } from 'lucide-react';
import AnimatedBackground from './AnimatedBackground';

const Pricing = () => {
  const { pricing } = useSite();
  const { t, lang } = useLang();
  const pricingRows = pricing || [];
  const P = (v) => pickI18n(v, lang);
  const scrollContact = () =>
    document.querySelector('#contacto')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="tarifas" className="relative py-20 sm:py-28 bg-[#0a1622] overflow-hidden">
      <AnimatedBackground variant="mesh" intensity={0.4} />
      <div className="absolute right-0 top-0 w-[600px] h-[600px] rounded-full bg-[#00e0c7]/[0.04] blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00e0c7] mb-4">
            {t('sections.pricing')}
          </div>
          <h2 className="font-display text-white text-2xl sm:text-3xl lg:text-[52px] font-light tracking-tight" style={{ lineHeight: 1.25 }}>
            {t('sections.pricingTitle')}{' '}
            <span className="italic text-[#5eead4]">{t('sections.pricingHighlight')}</span>{' '}
            {t('sections.pricingTitleEnd')}
          </h2>
          <p className="mt-5 sm:mt-6 text-white/60 text-[15px] sm:text-[16px] leading-relaxed">
            {t('sections.pricingDescription')}
          </p>
        </div>

        {/* Table */}
        <div className="rounded-3xl border border-white/8 bg-white/[0.02] backdrop-blur-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-8 py-5 border-b border-white/5 text-[11px] uppercase tracking-[0.2em] text-white/40">
            <div className="col-span-3">{t('sections.pricingTableService')}</div>
            <div className="col-span-4">{t('sections.pricingTableModality')}</div>
            <div className="col-span-3">{t('sections.pricingTableRate')}</div>
            <div className="col-span-2 text-right">{t('sections.pricingTableAction')}</div>
          </div>

          {pricingRows.map((row, i) => (
            <div
              key={i}
              className="group grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4 px-5 sm:px-6 md:px-8 py-6 sm:py-7 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors items-start md:items-center"
            >
              <div className="md:col-span-3">
                <div className="text-[10px] text-[#00e0c7] uppercase tracking-widest mb-1 md:hidden">
                  {t('sections.pricingTableService')}
                </div>
                <div className="text-white font-medium text-[15px] sm:text-[16px]">{P(row.service)}</div>
              </div>
              <div className="md:col-span-4 text-white/60 text-[13px] sm:text-[14px]">{P(row.modality)}</div>
              <div className="md:col-span-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00e0c7]/10 border border-[#00e0c7]/20 text-[#9be8dc] text-[11px] sm:text-[12px] font-medium">
                  <Sparkles className="w-3 h-3" />
                  {P(row.rate)}
                </span>
              </div>
              <div className="md:col-span-2 md:text-right">
                <button
                  onClick={scrollContact}
                  className="inline-flex items-center gap-1.5 text-[#00e0c7] text-[12px] sm:text-[13px] font-medium hover:gap-2.5 transition-all group/btn"
                >
                  {P(row.cta)}
                  <ArrowUpRight className="w-3.5 h-3.5 group-hover/btn:rotate-45 transition-transform" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* CTA card */}
        <div className="mt-8 sm:mt-10 rounded-3xl bg-gradient-to-br from-[#0d1b2a] to-[#142838] border border-[#00e0c7]/20 p-8 sm:p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center gap-6 sm:gap-8">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#00e0c7] mb-3">
              {t('sections.pricingCustomBadge')}
            </div>
            <h3 className="font-display text-white text-xl sm:text-2xl lg:text-3xl font-light leading-snug">
              {t('sections.pricingCustomTitle')}
            </h3>
          </div>
          <button
            onClick={scrollContact}
            className="flex-shrink-0 group inline-flex items-center gap-2 px-6 sm:px-8 h-12 sm:h-14 rounded-full bg-[#00e0c7] hover:bg-[#00c4ad] text-[#0d1b2a] font-semibold text-[14px] sm:text-[15px] shadow-[0_10px_40px_-10px_rgba(0,224,199,0.5)] transition-all"
          >
            {t('cta.requestStudy')}
            <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
